<template>
  <v-container fluid class="index-container">
    <front-navigation></front-navigation>

    <br>

    <div class="booking-wrapper">
      <transition name="fade">
        <div class="sidebar-overlay" v-if="sidebarOpen" @click="closeSidebar"></div>
      </transition>

      <transition name="slide">
        <div class="sidebar-wrapper" v-show="sidebarOpen">
          <div class="sidebar-header">
            <h3 class="sidebar-title">
              <v-icon small class="mr-1">mdi-cart</v-icon>
              Your Booking Cart
              <span v-if="cartItemCount > 0" class="sidebar-badge">{{ cartItemCount }}</span>
            </h3>
            <v-btn icon small @click="closeSidebar" class="close-sidebar-btn">
              <v-icon>mdi-close-circle</v-icon>
            </v-btn>
          </div>
          <side-bar ref="sidebar" :deletable-categories="deletableCategories"></side-bar>
        </div>
      </transition>

      <div class="main-content">
        <!-- Wildlife Carousel - Desktop Only -->
        <div class="wildlife-carousel-wrapper" v-if="$vuetify.breakpoint.mdAndUp">
          <v-carousel v-model="carouselIndex"
                      cycle
                      interval="5000"
                      show-arrows="hover"
                      hide-delimiter-background
                      delimiter-icon="mdi-circle"
                      height="320"
                      class="wildlife-carousel">
            <v-carousel-item v-for="(image, idx) in wildlifeImages"
                             :key="idx"
                             :src="image.src"
                             class="wildlife-carousel-item">
              <div class="wildlife-overlay">
                <div class="wildlife-caption">
                  <h2>{{ image.title }}</h2>
                  <p>{{ image.subtitle }}</p>
                </div>
              </div>
            </v-carousel-item>
          </v-carousel>
        </div>

        <v-card flat>
          <div class="booking-stepper">
            <div class="stepper-header">
              <div v-for="(step, index) in steps"
                   :key="index"
                   class="step-item"
                   :class="{ active: currentStep === index, completed: currentStep > index, clickable: currentStep > index }"
                   @click="currentStep > index ? goToStep(index) : null">
                <div class="step-circle">
                  <span v-if="currentStep > index" class="step-check">✓</span>
                  <span v-else class="step-number">{{ index + 1 }}</span>
                </div>
                <div class="step-label">{{ step.label }}</div>
                <div v-if="index < steps.length - 1" class="step-line"></div>
              </div>
            </div>

            <div class="stepper-content">
              <div v-show="currentStep === 0" class="step-panel">
                <div class="step-actions-top">
                  <v-btn color="#74a533"
                         dark
                         @click="nextStep"
                         :disabled="!isDateSelectionValid"
                         class="step-btn">
                    Continue to Guests
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <div class="step-body">
                  <div class="step-header-section">
                    <h2 class="step-title">
                      <v-icon class="step-icon" color="#74a533">mdi-calendar-range</v-icon>
                      Select Your Dates
                    </h2>
                    <p class="step-subtitle">Choose your check‑in and check‑out dates</p>
                  </div>
                  <v-card flat class="step-card">
                    <v-container>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-menu ref="checkInMenu"
                                  v-model="checkInMenu"
                                  :close-on-content-click="false"
                                  transition="scale-transition"
                                  offset-y
                                  min-width="290px">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field v-model="checkInDate"
                                            label="Check‑in Date"
                                            prepend-icon="mdi-calendar"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            outlined
                                            dense
                                            class="date-picker-field"
                                            :error-messages="checkInError"
                                            @input="validateDates"></v-text-field>
                            </template>
                            <v-date-picker ref="checkInPicker"
                                           v-model="checkInDate"
                                           :min="today"
                                           :max="maxDate"
                                           @input="onCheckInSelect"
                                           locale="en"
                                           color="#74a533"></v-date-picker>
                          </v-menu>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-menu ref="checkOutMenu"
                                  v-model="checkOutMenu"
                                  :close-on-content-click="false"
                                  transition="scale-transition"
                                  offset-y
                                  min-width="290px">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field v-model="checkOutDate"
                                            label="Check‑out Date"
                                            prepend-icon="mdi-calendar"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            outlined
                                            dense
                                            class="date-picker-field"
                                            :error-messages="checkOutError"
                                            @input="validateDates"></v-text-field>
                            </template>
                            <v-date-picker ref="checkOutPicker"
                                           v-model="checkOutDate"
                                           :min="minCheckOutDate"
                                           :max="maxDate"
                                           @input="onCheckOutSelect"
                                           locale="en"
                                           color="#74a533"></v-date-picker>
                          </v-menu>
                        </v-col>
                      </v-row>
                      <v-row v-if="dateValidationMessage">
                        <v-col cols="12">
                          <v-alert type="error" dense text>{{ dateValidationMessage }}</v-alert>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card>
                </div>
              </div>

              <div v-show="currentStep === 1" class="step-panel">
                <div class="step-actions-top">
                  <v-btn text @click="prevStep" class="step-btn-back">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-btn color="#74a533"
                         dark
                         @click="nextStep"
                         :disabled="!isGuestSelectionValid"
                         class="step-btn">
                    Continue to Vehicles
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <div class="step-body">
                  <div class="step-header-section">
                    <h2 class="step-title">
                      <v-icon class="step-icon" color="#74a533">mdi-account-group</v-icon>
                      Guest Information
                    </h2>
                    <p class="step-subtitle">Select the number of guests and their residency types</p>
                  </div>
                  <v-card flat class="step-card">
                    <v-container>
                      <v-row>
                        <v-col cols="12">
                          <guests ref="guestsComponent"
                                  @guests-auto-updated="handleGuestsUpdated"></guests>
                          <div class="mt-3 text-center">
                            <span class="grey--text">Use the dropdown above to add guests and select their residency.</span>
                          </div>
                        </v-col>
                      </v-row>
                      <v-row v-if="guestValidationMessage">
                        <v-col cols="12">
                          <v-alert type="error" dense text>{{ guestValidationMessage }}</v-alert>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <div class="guest-summary">
                            <span class="guest-total">Total Guests: {{ totalGuests }}</span>
                            <span v-if="totalGuests > 0" class="guest-badge">{{ totalGuests }} guest{{ totalGuests > 1 ? 's' : '' }}</span>
                          </div>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card>
                </div>
              </div>

              <div v-show="currentStep === 2" class="step-panel">
                <div class="step-actions-top">
                  <v-btn text @click="prevStep" class="step-btn-back">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-btn color="#74a533"
                         dark
                         @click="nextStep"
                         :disabled="!isVehicleConditionMet"
                         class="step-btn">
                    Continue to Activities
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <div class="step-body">
                  <div class="step-header-section">
                    <h2 class="step-title">
                      <v-icon class="step-icon" color="#74a533">mdi-car</v-icon>
                      Vehicle Details
                    </h2>
                    <p class="step-subtitle">Add vehicle(s) or mark if already booked separately</p>
                  </div>
                  <v-card flat class="step-card">
                    <v-container>
                      <v-row>
                        <v-col cols="12">
                          <vehicle ref="vehicleComponent"
                                   @vehicles-auto-updated="handleVehiclesUpdated"></vehicle>
                          <div class="mt-3 text-center">
                            <span class="grey--text">Use the dropdown to add vehicle types and counts.</span>
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <div class="vehicle-summary">
                            <span class="vehicle-total">Total Vehicles: {{ totalVehicles }}</span>
                            <span v-if="vehicleFeeSeparate" class="vehicle-badge">Vehicle fee booked separately</span>
                          </div>
                        </v-col>
                      </v-row>
                      <v-row v-if="!isVehicleConditionMet" class="mt-2">
                        <v-col cols="12">
                          <v-alert type="warning" dense text>
                            <v-icon left small>mdi-alert</v-icon>
                            Please add at least one vehicle or check "Vehicle fee already booked separately"
                          </v-alert>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card>
                </div>
              </div>

              <div v-show="currentStep === 3" class="step-panel">
                <div class="step-actions-top">
                  <v-btn text @click="prevStep" class="step-btn-back">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-btn text
                         @click="skipActivities"
                         :disabled="!isVehicleConditionMet"
                         class="step-btn-skip">
                    Skip Activities
                  </v-btn>
                  <v-btn color="#74a533"
                         dark
                         @click="nextStep"
                         :disabled="!isVehicleConditionMet"
                         class="step-btn">
                    Continue to Accommodation
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <div class="step-body">
                  <div class="step-header-section">
                    <h2 class="step-title">
                      <v-icon class="step-icon" color="#74a533">mdi-hiking</v-icon>
                      Activities
                    </h2>
                    <p class="step-subtitle">Add activities to your booking (optional)</p>
                  </div>
                  <v-card flat class="step-card">
                    <activities ref="activitiesComponent"></activities>
                  </v-card>
                  <v-row v-if="!isVehicleConditionMet" class="mt-2">
                    <v-col cols="12">
                      <v-alert type="warning" dense text>
                        <v-icon left small>mdi-alert</v-icon>
                        Please go back to Vehicles and add a vehicle or check the separate fee box.
                      </v-alert>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <div v-show="currentStep === 4" class="step-panel">
                <div class="step-actions-top">
                  <v-btn text @click="prevStep" class="step-btn-back">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-btn text @click="skipAccommodation" class="step-btn-skip">
                    Skip Accommodation
                  </v-btn>
                  <v-btn color="#74a533"
                         dark
                         @click="nextStep"
                         class="step-btn">
                    Continue to Camping
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <div class="step-body">
                  <div class="step-header-section">
                    <h2 class="step-title">
                      <v-icon class="step-icon" color="#74a533">mdi-hotel</v-icon>
                      Accommodation
                    </h2>
                    <p class="step-subtitle">Add accommodation to your booking (optional)</p>
                  </div>
                  <v-card flat class="step-card">
                    <accomodations ref="accommodationComponent"></accomodations>
                  </v-card>
                </div>
              </div>

              <div v-show="currentStep === 5" class="step-panel">
                <div class="step-actions-top">
                  <v-btn text @click="prevStep" class="step-btn-back">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-btn text @click="skipCamping" class="step-btn-skip">
                    Skip Camping
                  </v-btn>
                  <v-btn color="#74a533"
                         dark
                         @click="nextStep"
                         class="step-btn">
                    Continue to Terms
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <div class="step-body">
                  <div class="step-header-section">
                    <h2 class="step-title">
                      <v-icon class="step-icon" color="#74a533">mdi-tent</v-icon>
                      Camping
                    </h2>
                    <p class="step-subtitle">Add a camping site to your booking (optional)</p>
                  </div>
                  <v-card flat class="step-card">
                    <camp-sites ref="campingComponent"></camp-sites>
                  </v-card>
                </div>
              </div>

              <div v-show="currentStep === 6" class="step-panel">
                <div class="step-actions-top">
                  <v-btn text @click="prevStep" class="step-btn-back">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-btn color="#74a533"
                         dark
                         @click="openCartSidebar"
                         class="step-btn">
                    Proceed to Summary
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
                <div class="step-body terms-body">
                  <div class="step-header-section">
                    <h2 class="step-title">
                      <v-icon class="step-icon" color="#74a533">mdi-file-document</v-icon>
                      Terms and Conditions
                    </h2>
                    <p class="step-subtitle">Please review the following terms before proceeding</p>
                  </div>
                  <v-card flat class="step-card terms-card">
                    <v-container>
                      <v-row>
                        <v-col cols="12">
                          <h3 class="section-title">General Terms</h3>
                          <p class="terms-text">
                            1. By entering Ol Pejeta Conservancy (the Conservancy) for any purpose, I acknowledge the inherent dangers to my life and property. I confirm that I have read and will abide by the Conservancys Code of Conduct to ensure reasonable safety.<br />
                            2. I understand and accept that the Conservancy contains dangerous wild animals, including but not limited to lions, rhinos, elephants, buffaloes, snakes, and insects. By entering the Conservancy, I waive and release Ol Pejeta Conservancy, Ol Pejeta Conservancy Limited, and any of its employees, agents, affiliates, and officers from all liability, claims, accidents, losses, or damages, including personal injury (fatal or otherwise) and damage to personal property, that may arise as a result of my entry.<br />
                            3. This waiver is entered on behalf of myself and any minors accompanying me and is binding on my assigns, heirs, and legal representatives. If any portion of this waiver is unenforceable, that portion shall be struck out, but the remaining portion shall remain in full force and effect. This waiver is governed by Kenyan laws.
                          </p>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <h3 class="section-title">Code of Conduct</h3>
                          <v-list class="terms-list">
                            <v-list-item v-for="(rule, index) in codeOfConduct" :key="index">
                              <v-list-item-content>
                                <v-list-item-title class="terms-list-item">{{ index + 1 }}. {{ rule }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <h3 class="section-title">Bookings</h3>
                          <p class="terms-text">
                            1. All bookings to the Conservancy shall be made through the Online Booking and Ticketing System (OBTS).<br />
                            2. Payments must reference a Booking ID as indicated in the invoice issued by the Conservancy.<br />
                            3. Payments made without a Booking ID must be notified to the Conservancy within seven days from the date of remittance for assignment of a Booking ID.<br />
                            4. Payments without a Booking ID and not notified will be forfeited if not utilized within three months from the date of remittance.
                          </p>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <h3 class="section-title">Booking Cancellation Policy</h3>
                          <v-list class="terms-list">
                            <v-list-item v-for="(policy, index) in cancellationPolicies" :key="index">
                              <v-list-item-content>
                                <v-list-item-title class="terms-list-item">{{ index + 1 }}. {{ policy }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <h3 class="section-title">Conservation Experiences Policy</h3>
                          <v-list class="terms-list">
                            <v-list-item v-for="(policy, index) in conservationPolicies" :key="index">
                              <v-list-item-content>
                                <v-list-item-title class="terms-list-item">{{ index + 1 }}. {{ policy }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <h3 class="section-title">Payment Policy</h3>
                          <v-list class="terms-list">
                            <v-list-item v-for="(policy, index) in paymentPolicies" :key="index">
                              <v-list-item-content>
                                <v-list-item-title class="terms-list-item">{{ index + 1 }}. {{ policy }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card>
                </div>
              </div>

              <div v-show="currentStep === 7" class="step-panel">
                <div class="step-actions-top">
                  <v-btn text @click="prevStep" class="step-btn-back">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back
                  </v-btn>
                  <v-btn text @click="resetBooking" class="step-btn-reset">
                    <v-icon left>mdi-refresh</v-icon>
                    Reset
                  </v-btn>
                </div>
                <div class="step-body">
                  <div class="step-header-section">
                    <h2 class="step-title">
                      <v-icon class="step-icon" color="#74a533">mdi-credit-card-check</v-icon>
                      Booking Summary
                    </h2>
                    <p class="step-subtitle">Review your booking and proceed to payment</p>
                  </div>
                  <v-card flat class="step-card checkout-card">
                    <v-container>
                      <v-row>
                        <v-col cols="12" md="6">
                          <h3 class="checkout-section-title">Booking Summary</h3>
                          <v-list class="checkout-summary-list">
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title><strong>Check‑in:</strong> {{ formattedCheckInDate }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title><strong>Check‑out:</strong> {{ formattedCheckOutDate }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title><strong>Guests:</strong> {{ totalGuests }} (Adults: {{ globalGuests.adults }}, Children: {{ globalGuests.children }})</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title><strong>Vehicles:</strong> {{ totalVehicles }} {{ vehicleFeeSeparate ? '(fee booked separately)' : '' }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item v-if="getActivityCount > 0">
                              <v-list-item-content>
                                <v-list-item-title><strong>Activities:</strong> {{ getActivityCount }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item v-if="getAccommodationCount > 0">
                              <v-list-item-content>
                                <v-list-item-title><strong>Accommodation:</strong> {{ getAccommodationCount }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                            <v-list-item v-if="getCampingCount > 0">
                              <v-list-item-content>
                                <v-list-item-title><strong>Camping:</strong> {{ getCampingCount }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-col>
                        <v-col cols="12" md="6">
                          <h3 class="checkout-section-title">Total Cost</h3>
                          <div class="checkout-total">
                            <div class="total-amount">KES {{ calculateTotal }}</div>
                            <v-btn color="#74a533"
                                   dark
                                   large
                                   block
                                   class="checkout-btn"
                                   @click="proceedToCheckout">
                              <v-icon left>mdi-lock</v-icon>
                              Proceed to Payment
                            </v-btn>
                          </div>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card>
                </div>
              </div>
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <UserGuide ref="userGuide" @finished="onGuideFinished" />

    <v-snackbar v-model="protectedNotice"
                :timeout="4200"
                color="#c62828"
                bottom
                right
                rounded
                class="protected-snackbar">
      <v-icon left small color="white">mdi-lock</v-icon>
      {{ protectedNoticeText }}
      <template v-slot:action="{ attrs }">
        <v-btn text small v-bind="attrs" @click="protectedNotice = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <div class="help-btn-wrapper">
      <v-btn fab small class="help-btn" @click="showGuide">
        <v-icon>mdi-help-circle</v-icon>
      </v-btn>
    </div>
  </v-container>
</template>

<script>
  import CallToAction from '../Repeated/CallToAction.vue'
  import Accomodations from '../Repeated/Accomodation'
  import CampSites from '../Repeated/CampSites'
  import Activities from '../Repeated/Activities'
  import SideBar from '../Repeated/SideBar'
  import UserGuide from '../Repeated/UserGuide'
  import Guests from '../Repeated/Guests'
  import Vehicle from '../Repeated/Vehicle'

  export default {
    name: 'index',
    components: {
      CallToAction,
      Activities,
      CampSites,
      Accomodations,
      UserGuide,
      SideBar,
      Guests,
      Vehicle
    },
    data() {
      return {
        currentStep: 0,
        steps: [
          { label: 'Dates' },
          { label: 'Guests' },
          { label: 'Vehicles' },
          { label: 'Activities' },
          { label: 'Accommodation' },
          { label: 'Camping' },
          { label: 'Terms' },
          { label: 'Summary' }
        ],
        checkInDate: null,
        checkOutDate: null,
        checkInMenu: false,
        checkOutMenu: false,
        today: new Date().toISOString().slice(0, 10),
        maxDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10),
        checkInError: '',
        checkOutError: '',
        dateValidationMessage: '',
        totalGuests: 0,
        totalVehicles: 0,
        vehicleFeeSeparate: false,
        guestValidationMessage: '',
        sidebarOpen: false,
        cartItemCount: 0,
        cartInterval: null,
        unsubscribe: null,
        showCartButton: false,
        // ------------------------------------------------------------------
        // Deletion guard: only activities and accommodation may be removed
        // from the booking summary (cart) modal popup. Vehicles, conservation
        // fees and camping sites must be edited or deleted from the steps
        // section of the booking flow.
        // ------------------------------------------------------------------
        deletableCategories: ['activity', 'accommodation'],
        deletionGuardInterval: null,
        deletionGuardBusy: false,
        protectedSnapshot: {
          vehicle_data: [],
          conservation_data: [],
          camping_facilities: []
        },
        protectedNotice: false,
        protectedNoticeText: '',
        codeOfConduct: [
          'Strict speed limit of maximum 40 KPH.',
          'Do not discard litter or cigarette butts.',
          'Off‑road driving is strictly prohibited; keep to designated roads or tracks.',
          'Beware of dangerous wildlife and do not get out of the car unless in a designated area.',
          'Wildlife always have the right of way; do not chase, harass, or follow animals.',
          'Do not feed wildlife from your car, in the wild, or at your camp or lodge.',
          'Do not sit or stand on the vehicles roof at any time.',
          'Collection of firewood is strictly prohibited.',
          'Do not damage or remove any flora or fauna, including plants or bones.',
          'No driving is allowed in the Conservancy past 7:00 PM.',
          'No firearms, spears, bows, or arrows of any kind are to be carried within the Conservancy.',
          'Ensure you obtain an official receipt for any cash transaction.',
          'No use of private drones unless authorized.'
        ],
        cancellationPolicies: [
          'If a booking is cancelled 61 days or more prior to arrival, 20% of the total booking value will be charged as a cancellation fee.',
          'If a booking is cancelled 60 to 31 days prior to arrival, 40% of the total booking value will be charged as a cancellation fee.',
          'If a booking is cancelled 30 to 8 days prior to arrival, 50% of the total booking value will be charged as a cancellation fee.',
          'If a booking is cancelled 7 days or less prior to arrival or in case of a No Show, 100% of the total booking value will be charged as a cancellation fee.'
        ],
        conservationPolicies: [
          'If a client arrives 15 minutes after the scheduled starting time of an activity without prior notice, OPC reserves the right to reduce the duration of the experience by 15 minutes or cancel the booking without a refund.',
          'If a client does not arrive within 30 minutes after the scheduled starting time of an activity without notice, the booking will be cancelled, and payment will be forfeited.'
        ],
        paymentPolicies: [
          'If a booking is made within seven days of arrival, full payment is expected immediately after making the reservation.',
          'If a booking is made at least one month prior to arrival, a 30% deposit is required within 7 days, with the balance paid in full upon arrival at the gate.',
          'All bank transfer charges are to be borne by the guest.',
          'Payments made through bank transfer must be completed 3 days before arrival.'
        ],
        // Wildlife Carousel - Ol Pejeta specific
        carouselIndex: 0,
        wildlifeImages: [
          {
            src: 'https://www.olpejetaconservancy.org/wp-content/uploads/2024/07/Fatu-with-Najin-scaled.webp',
            title: 'Najin and Fatu',
            subtitle: 'Home to the last two northern white rhinos on Earth'
          },
          {
            src: 'https://www.olpejetaconservancy.org/wp-content/uploads/2024/07/Socrates-with-Pasaka.webp',
            title: 'Chimpanzee Sanctuary',
            subtitle: 'Visit the only chimpanzee sanctuary in Kenya'
          },
          {
            src: 'https://www.olpejetaconservancy.org/wp-content/uploads/2024/06/Dusty-Elephant_3-1-scaled.jpg',
            title: 'Elephant Herds',
            subtitle: 'Witness majestic elephants roaming the savanna'
          },
          {
            src: 'https://www.olpejetaconservancy.org/wp-content/uploads/2024/06/DSC_6229-1-scaled.jpg',
            title: 'The Majestic Giraffe',
            subtitle: ''
          },
          {
            src: 'https://www.olpejetaconservancy.org/wp-content/uploads/2024/07/Zebra-3-scaled.webp',
            title: 'Zebra',
            subtitle: ''
          }
        ]
      }
    },
    computed: {
      cartItems() {
        const activityData = JSON.parse(localStorage.getItem('activity_data') || '[]')
        const facilityData = JSON.parse(localStorage.getItem('facility_data') || '[]')
        const conservationData = JSON.parse(localStorage.getItem('conservation_data') || '[]')
        const vehicleData = JSON.parse(localStorage.getItem('vehicle_data') || '[]')
        return activityData.length + facilityData.length + conservationData.length + vehicleData.length
      },
      minCheckOutDate() {
        return this.checkInDate || this.today
      },
      isDateSelectionValid() {
        return this.checkInDate && this.checkOutDate && this.checkOutDate >= this.checkInDate
      },
      formattedCheckInDate() {
        if (!this.checkInDate) return 'Not selected'
        return new Date(this.checkInDate).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
      },
      formattedCheckOutDate() {
        if (!this.checkOutDate) return 'Not selected'
        return new Date(this.checkOutDate).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
      },
      globalGuests() {
        if (this.$store.state.GlobalBookingStore && this.$store.state.GlobalBookingStore.booking_guests) {
          return this.$store.state.GlobalBookingStore.booking_guests
        }
        return { adults: 0, children: 0, total: 0 }
      },
      isGuestSelectionValid() {
        let guestTotal = this.totalGuests
        if (guestTotal === 0 && this.globalGuests.total > 0) {
          guestTotal = this.globalGuests.total
        }
        if (guestTotal === 0) {
          try {
            const guestsData = JSON.parse(localStorage.getItem('guests') || '{"adults":0,"children":0}')
            const total = guestsData.total !== undefined ? guestsData.total : (guestsData.adults || 0) + (guestsData.children || 0)
            guestTotal = total
          } catch (e) {
            guestTotal = 0
          }
        }
        return guestTotal > 0
      },
      isVehicleConditionMet() {
        return this.totalVehicles > 0 || this.vehicleFeeSeparate
      },
      getActivityCount() {
        const activityData = JSON.parse(localStorage.getItem('activity_data') || '[]')
        return activityData.length
      },
      getAccommodationCount() {
        const facilityData = JSON.parse(localStorage.getItem('facility_data') || '[]')
        return facilityData.filter(f => f.facility_type !== 'Camping Sites').length
      },
      getCampingCount() {
        const facilityData = JSON.parse(localStorage.getItem('facility_data') || '[]')
        return facilityData.filter(f => f.facility_type === 'Camping Sites').length
      },
      calculateTotal() {
        let total = 0
        const activityData = JSON.parse(localStorage.getItem('activity_data') || '[]')
        const facilityData = JSON.parse(localStorage.getItem('facility_data') || '[]')
        const conservationData = JSON.parse(localStorage.getItem('conservation_data') || '[]')
        const vehicleData = JSON.parse(localStorage.getItem('vehicle_data') || '[]')
        activityData.forEach(item => { total += parseFloat(item.price || item.total_price || 0) })
        facilityData.forEach(item => { total += parseFloat(item.price || item.total_price || 0) })
        conservationData.forEach(item => { total += parseFloat(item.price || item.total_price || 0) })
        vehicleData.forEach(item => { total += parseFloat(item.price || item.total_price || 0) })
        return total.toFixed(2)
      }
    },
    watch: {
      cartItems: {
        handler(newVal) {
          if (newVal > 0 && this.showCartButton && !this.sidebarOpen) {
            this.sidebarOpen = true
            document.body.style.overflow = 'hidden'
          }
          this.refreshSidebarContent()
        },
        immediate: false
      },
      globalGuests: {
        handler(newVal) {
          if (newVal && newVal.total !== undefined) {
            this.totalGuests = newVal.total
            this.guestValidationMessage = this.totalGuests > 0 ? '' : 'Please add at least one guest'
            this.checkCartButtonVisibility()
            this.refreshSidebarContent()
          }
        },
        immediate: true,
        deep: true
      },
      totalGuests: {
        handler(newVal) {
          this.guestValidationMessage = newVal > 0 ? '' : 'Please add at least one guest'
          this.checkCartButtonVisibility()
          this.refreshSidebarContent()
        },
        immediate: true
      },
      checkInDate: {
        handler(newVal, oldVal) {
          if (newVal !== oldVal) {
            this.validateDates()
            if (this.isDateSelectionValid) {
              this.saveDateSelection()
            } else {
              this.$store.commit('REMOVE_AVAILABLE_FACILITIES')
            }
            this.refreshSidebarContent()
          }
        },
        immediate: false
      },
      checkOutDate: {
        handler(newVal, oldVal) {
          if (newVal !== oldVal) {
            this.validateDates()
            if (this.isDateSelectionValid) {
              this.saveDateSelection()
            } else {
              this.$store.commit('REMOVE_AVAILABLE_FACILITIES')
            }
            this.refreshSidebarContent()
          }
        },
        immediate: false
      },
      vehicleFeeSeparate: {
        handler() {
          this.checkCartButtonVisibility()
          this.refreshSidebarContent()
        },
        immediate: false
      },
      // ------------------------------------------------------------------
      // The booking summary (cart) modal popup is the only place where the
      // deletion guard runs. While it is open, vehicles, conservation fees
      // and camping sites are locked; only activities and accommodation may
      // be removed. To edit or delete a locked item, the user must return
      // to the relevant step in the booking flow.
      // ------------------------------------------------------------------
      sidebarOpen: {
        handler(newVal) {
          if (newVal) {
            this.startDeletionGuard()
          } else {
            this.stopDeletionGuard()
          }
        },
        immediate: false
      }
    },
    created() {
      this.fetchInitialData()
      this.initializeStorage()
      this.loadStateFromStorage()
      this.loadDatesFromGlobalStore()
      this.commitStoredDataToVuex()
      this.updateCartCount()
      this.checkCartButtonVisibility()

      this.cartInterval = setInterval(() => {
        this.updateCartCount()
        this.checkCartButtonVisibility()
        this.refreshSidebarContent()
      }, 2000)

      if (this.$store && this.$store.subscribe) {
        this.unsubscribe = this.$store.subscribe((mutation) => {
          if (mutation.type && (
            mutation.type.includes('ACTIVITIES') ||
            mutation.type.includes('FACILITIES') ||
            mutation.type.includes('VEHICLES') ||
            mutation.type.includes('CONSERVATION') ||
            mutation.type.includes('VEHICLE_SEPARATE') ||
            mutation.type.includes('DISABLED_STATUS')
          )) {
            this.updateCartCount()
            this.checkCartButtonVisibility()
            this.refreshSidebarContent()
          }
        })
      }
      this.$root.$on('open-sidebar', this.openSidebarFromEvent)
      this.$root.$on('cart-updated', this.handleCartUpdated)
      window.addEventListener('storage', this.handleStorageChange)
    },
    mounted() {
      this.$nextTick(() => {
        this.loadStateFromStorage()
        this.loadDatesFromGlobalStore()
        this.checkCartButtonVisibility()
        this.refreshSidebarContent()
      })
    },
    beforeDestroy() {
      if (this.cartInterval) clearInterval(this.cartInterval)
      if (this.unsubscribe) this.unsubscribe()
      this.stopDeletionGuard()
      this.$root.$off('open-sidebar', this.openSidebarFromEvent)
      this.$root.$off('cart-updated', this.handleCartUpdated)
      window.removeEventListener('storage', this.handleStorageChange)
    },
    sockets: {
      connect: function () { },
      mpesa: function () { },
      customEmit: function () { }
    },
    methods: {
      fetchInitialData() {
        const actions = [
          'get_all_vehicle_fees',
          'get_all_conservation_fees',
          'get_active_activities',
          'get_active_accommodation',
          'get_active_camping'
        ]
        actions.forEach(a => {
          if (this.$store.dispatch && this.$store._actions && this.$store._actions[a]) {
            this.$store.dispatch(a).catch(err => console.warn(`${a} fetch error:`, err))
          }
        })
      },

      // ==================================================================
      // DELETION GUARD HELPERS
      // ==================================================================

      /**
       * Read a localStorage key and always return an array.
       */
      safeParseArray(key, fallback) {
        const empty = Array.isArray(fallback) ? fallback : []
        try {
          const raw = localStorage.getItem(key)
          if (!raw) return empty.slice()
          const parsed = JSON.parse(raw)
          return Array.isArray(parsed) ? parsed : empty.slice()
        } catch (e) {
          return empty.slice()
        }
      },

      /**
       * Build a stable identity key for a stored booking item so that the
       * guard can tell exactly which entries disappeared.
       */
      itemKey(item) {
        if (item === null || item === undefined) return ''
        if (typeof item !== 'object') return String(item)
        const candidates = [
          item.id,
          item.public_id,
          item.facility_id,
          item.activity_id,
          item.vehicle_id,
          item.conservation_id,
          item.fee_id,
          item.uuid,
          item.uid,
          item.code,
          item.name,
          item.activity_name,
          item.facility_name,
          item.vehicle_type,
          item.facility_type
        ]
        const found = candidates.find(v => v !== undefined && v !== null && v !== '')
        if (found !== undefined) return String(found)
        try {
          return JSON.stringify(item)
        } catch (e) {
          return ''
        }
      },

      /**
       * Multiset difference: returns the entries present in `snapshotItems`
       * that are no longer present in `currentItems`.
       */
      findMissingItems(snapshotItems, currentItems) {
        const pool = (currentItems || []).map(item => this.itemKey(item))
        const missing = []
          ; (snapshotItems || []).forEach(item => {
            const key = this.itemKey(item)
            const index = pool.indexOf(key)
            if (index > -1) {
              pool.splice(index, 1)
            } else {
              missing.push(item)
            }
          })
        return missing
      },

      /**
       * Remember every protected entry currently in the cart. This is taken
       * the moment the summary popup opens so that anything the user removes
       * from inside the popup can be put straight back.
       */
      captureProtectedSnapshot() {
        const facilities = this.safeParseArray('facility_data', [])
        this.protectedSnapshot = {
          vehicle_data: this.safeParseArray('vehicle_data', []),
          conservation_data: this.safeParseArray('conservation_data', []),
          camping_facilities: facilities.filter(f => f && f.facility_type === 'Camping Sites')
        }
      },

      /**
       * Start watching for protected deletions while the popup is open.
       */
      startDeletionGuard() {
        this.captureProtectedSnapshot()
        if (this.deletionGuardInterval) {
          clearInterval(this.deletionGuardInterval)
          this.deletionGuardInterval = null
        }
        this.deletionGuardInterval = setInterval(() => {
          this.enforceProtectedItems()
        }, 250)
      },

      /**
       * Stop watching and drop the snapshot when the popup closes.
       */
      stopDeletionGuard() {
        if (this.deletionGuardInterval) {
          clearInterval(this.deletionGuardInterval)
          this.deletionGuardInterval = null
        }
        this.deletionGuardBusy = false
        this.protectedSnapshot = {
          vehicle_data: [],
          conservation_data: [],
          camping_facilities: []
        }
      },

      /**
       * Re-instate any protected entry that was removed from the summary
       * popup. Activities and accommodation are intentionally untouched and
       * remain fully deletable.
       */
      enforceProtectedItems() {
        if (!this.sidebarOpen) return
        if (this.deletionGuardBusy) return
        this.deletionGuardBusy = true

        try {
          const restoredLabels = []

          // ---- Vehicles (locked) -------------------------------------
          const currentVehicles = this.safeParseArray('vehicle_data', [])
          const missingVehicles = this.findMissingItems(this.protectedSnapshot.vehicle_data, currentVehicles)
          if (missingVehicles.length > 0) {
            localStorage.setItem('vehicle_data', JSON.stringify(currentVehicles.concat(missingVehicles)))
            restoredLabels.push('vehicles')
          }

          // ---- Conservation fees (locked) ----------------------------
          const currentConservation = this.safeParseArray('conservation_data', [])
          const missingConservation = this.findMissingItems(this.protectedSnapshot.conservation_data, currentConservation)
          if (missingConservation.length > 0) {
            localStorage.setItem('conservation_data', JSON.stringify(currentConservation.concat(missingConservation)))
            restoredLabels.push('conservation fees')
          }

          // ---- Camping sites (locked) --------------------------------
          const currentFacilities = this.safeParseArray('facility_data', [])
          const missingCamping = this.findMissingItems(this.protectedSnapshot.camping_facilities, currentFacilities)
          if (missingCamping.length > 0) {
            localStorage.setItem('facility_data', JSON.stringify(currentFacilities.concat(missingCamping)))
            restoredLabels.push('camping sites')
          }

          if (restoredLabels.length > 0) {
            this.notifyProtectedBlocked(restoredLabels)
            this.commitStoredDataToVuex()
            this.loadStateFromStorage()
            this.updateCartCount()
            this.checkCartButtonVisibility()
            this.refreshSidebarContent()
            this.$root.$emit('cart-updated')
            // Re-sync so the guard does not keep fighting the same entries.
            this.captureProtectedSnapshot()
          }
        } finally {
          this.deletionGuardBusy = false
        }
      },

      /**
       * Tell the user why their deletion did not go through and point them
       * back to the steps section to edit or delete from there.
       */
      notifyProtectedBlocked(labels) {
        const unique = []
          ; (labels || []).forEach(label => {
            if (unique.indexOf(label) === -1) unique.push(label)
          })
        const readable = unique.join(' and ')
        this.protectedNoticeText = `${readable.charAt(0).toUpperCase()}${readable.slice(1)} cannot be deleted here. Please go back to the steps section to delete or edit from there.`
        this.protectedNotice = true
      },

      // ==================================================================
      // EXISTING HELPERS
      // ==================================================================

      loadStateFromStorage() {
        try {
          const guestsData = JSON.parse(localStorage.getItem('guests') || '{"adults":0,"children":0}')
          let total = 0
          if (guestsData.total !== undefined) {
            total = guestsData.total
          } else {
            total = (guestsData.adults || 0) + (guestsData.children || 0)
          }
          this.totalGuests = total
        } catch (e) {
          this.totalGuests = 0
        }

        try {
          const separate = localStorage.getItem('vehicle_separate')
          if (separate !== null) {
            this.vehicleFeeSeparate = JSON.parse(separate)
          } else {
            this.vehicleFeeSeparate = false
          }
        } catch (e) {
          this.vehicleFeeSeparate = false
        }

        try {
          const vehicleData = JSON.parse(localStorage.getItem('vehicle_data') || '[]')
          this.totalVehicles = vehicleData.length
        } catch (e) {
          this.totalVehicles = 0
        }

        if (this.$store.state.GlobalBookingStore) {
          const storeGuests = this.$store.state.GlobalBookingStore.booking_guests
          if (storeGuests && storeGuests.total !== undefined && storeGuests.total > this.totalGuests) {
            this.totalGuests = storeGuests.total
          }
          const storeVehicles = this.$store.state.GlobalBookingStore.booking_vehicles
          if (storeVehicles) {
            if (storeVehicles.total !== undefined) {
              this.totalVehicles = storeVehicles.total
            }
            if (storeVehicles.vehicleFeeSeparate !== undefined) {
              this.vehicleFeeSeparate = storeVehicles.vehicleFeeSeparate
            }
          }
        }

        this.guestValidationMessage = this.totalGuests > 0 ? '' : 'Please add at least one guest'
        this.refreshSidebarContent()
      },

      commitStoredDataToVuex() {
        const safeCommit = (type, payload) => {
          if (this.$store._mutations && this.$store._mutations[type]) {
            this.$store.commit(type, payload)
          }
        }
        try { safeCommit('CHOSEN_ACTIVITIES', JSON.parse(localStorage.getItem('activity_data') || '[]')) } catch (e) { }
        try { safeCommit('CHOSEN_FACILITIES', JSON.parse(localStorage.getItem('facility_data') || '[]')) } catch (e) { }
        try { safeCommit('CHOSEN_CONSERVATION_FEES', JSON.parse(localStorage.getItem('conservation_data') || '[]')) } catch (e) { }
        try { safeCommit('CHOSEN_VEHICLES', JSON.parse(localStorage.getItem('vehicle_data') || '[]')) } catch (e) { }
        try {
          const separate = localStorage.getItem('vehicle_separate')
          if (separate !== null) safeCommit('SET_VEHICLE_SEPARATE', JSON.parse(separate))
        } catch (e) { }
      },

      loadDatesFromGlobalStore() {
        if (this.$store.state.GlobalBookingStore && this.$store.state.GlobalBookingStore.booking_dates) {
          const globalDates = this.$store.state.GlobalBookingStore.booking_dates
          let changed = false
          if (globalDates.check_in && globalDates.check_in !== '' && globalDates.check_in !== this.checkInDate) {
            this.checkInDate = globalDates.check_in
            changed = true
          }
          if (globalDates.check_out && globalDates.check_out !== '' && globalDates.check_out !== this.checkOutDate) {
            this.checkOutDate = globalDates.check_out
            changed = true
          }
          if (changed) {
            this.validateDates()
            this.saveDateSelection()
          }
        }
        try {
          const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
          if (userData.check_in && !this.checkInDate) {
            this.checkInDate = userData.check_in
          }
          if (userData.check_out && !this.checkOutDate) {
            this.checkOutDate = userData.check_out
          }
          if (userData.check_in || userData.check_out) {
            this.validateDates()
            this.saveDateSelection()
          }
        } catch (e) { }
        this.refreshSidebarContent()
      },

      nextStep() {
        if (this.currentStep === 2 && !this.isVehicleConditionMet) {
          alert('Please add at least one vehicle or check "Vehicle fee already booked separately" before proceeding.')
          return
        }
        if (this.currentStep === 3 && !this.isVehicleConditionMet) {
          alert('Vehicle information is required. Please go back to the Vehicles step and add a vehicle or check the separate fee box.')
          return
        }
        if (this.currentStep < this.steps.length - 1) {
          this.currentStep++
        }
      },
      prevStep() {
        if (this.currentStep > 0) this.currentStep--
      },
      goToStep(index) {
        if (index <= this.currentStep) {
          this.currentStep = index
        }
      },
      skipActivities() {
        this.nextStep()
      },
      skipAccommodation() {
        if (this.currentStep === 4) {
          this.currentStep = 5
        }
      },
      skipCamping() {
        if (this.currentStep === 5) {
          this.currentStep = 6
        }
      },
      onCheckInSelect(date) {
        this.checkInDate = date
        this.checkInMenu = false
        if (this.checkOutDate && this.checkOutDate < this.checkInDate) {
          this.checkOutDate = null
        }
      },
      onCheckOutSelect(date) {
        this.checkOutDate = date
        this.checkOutMenu = false
      },
      validateDates() {
        this.checkInError = ''
        this.checkOutError = ''
        this.dateValidationMessage = ''
        if (this.checkInDate && this.checkOutDate) {
          if (this.checkOutDate < this.checkInDate) {
            this.dateValidationMessage = 'Check‑out date must be on or after check‑in date'
          } else {
            const checkIn = new Date(this.checkInDate)
            const checkOut = new Date(this.checkOutDate)
            const diffDays = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24))
            if (diffDays > 365) {
              this.dateValidationMessage = 'Booking duration cannot exceed 365 days'
            }
          }
        }
      },

      saveDateSelection() {
        if (!this.isDateSelectionValid) return
        const diffDays = Math.ceil((new Date(this.checkOutDate) - new Date(this.checkInDate)) / (1000 * 60 * 60 * 24))
        const dates = {
          check_in: this.checkInDate,
          check_out: this.checkOutDate,
          days: diffDays
        }

        if (this.$store.dispatch && this.$store.state.GlobalBookingStore) {
          this.$store.dispatch('GlobalBookingStore/updateBookingDates', dates)
        }

        this.$root.$emit('booking-dates-changed', dates)

        try {
          const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
          userData.check_in = this.checkInDate
          userData.check_out = this.checkOutDate
          localStorage.setItem('user_data', JSON.stringify(userData))
        } catch (e) { }
      },

      handleGuestsUpdated(guestsData) {
        this.totalGuests = guestsData.total || 0
        this.updateCartCount()
        this.checkCartButtonVisibility()
        this.guestValidationMessage = this.totalGuests > 0 ? '' : 'Please add at least one guest'
        this.refreshSidebarContent()
      },
      handleVehiclesUpdated(vehiclesData) {
        this.totalVehicles = vehiclesData.total || 0
        this.vehicleFeeSeparate = vehiclesData.vehicleFeeSeparate || false
        this.updateCartCount()
        this.checkCartButtonVisibility()
        this.refreshSidebarContent()
      },
      proceedToCheckout() {
        if (this.$router) {
          this.$router.push('/booking-info')
        } else {
          this.$root.$emit('proceed-to-checkout')
        }
      },
      resetBooking() {
        if (confirm('Are you sure you want to reset your booking? All data will be cleared.')) {
          this.stopDeletionGuard()
          localStorage.removeItem('activity_data')
          localStorage.removeItem('facility_data')
          localStorage.removeItem('conservation_data')
          localStorage.removeItem('vehicle_data')
          localStorage.removeItem('user_data')
          localStorage.removeItem('vehicle_separate')
          localStorage.removeItem('guests')
          localStorage.removeItem('booking')
          const safeCommit = (type, payload) => {
            if (this.$store._mutations && this.$store._mutations[type]) {
              this.$store.commit(type, payload)
            }
          }
          safeCommit('CHOSEN_ACTIVITIES', [])
          safeCommit('CHOSEN_FACILITIES', [])
          safeCommit('CHOSEN_CONSERVATION_FEES', [])
          safeCommit('CHOSEN_VEHICLES', [])
          this.$root.$emit('cart-updated')
          this.currentStep = 0
          this.checkInDate = null
          this.checkOutDate = null
          this.totalGuests = 0
          this.totalVehicles = 0
          this.vehicleFeeSeparate = false
          this.initializeStorage()
          this.loadStateFromStorage()
          this.loadDatesFromGlobalStore()
          this.commitStoredDataToVuex()
          this.refreshSidebarContent()
        }
      },
      toggleSidebar() {
        this.sidebarOpen = !this.sidebarOpen
        if (this.sidebarOpen) {
          document.body.style.overflow = 'hidden'
          this.refreshSidebarContent()
        } else {
          document.body.style.overflow = ''
        }
      },
      closeSidebar() {
        this.sidebarOpen = false
        document.body.style.overflow = ''
      },
      openSidebarFromEvent() {
        this.sidebarOpen = true
        document.body.style.overflow = 'hidden'
        this.updateCartCount()
        this.refreshSidebarContent()
      },
      openCartSidebar() {
        this.sidebarOpen = true
        document.body.style.overflow = 'hidden'
        this.updateCartCount()
        this.refreshSidebarContent()
      },
      checkCartButtonVisibility() {
        const hasGuests = this.totalGuests > 0
        const hasVehicles = this.totalVehicles > 0
        let isVehicleFeeSeparate = this.vehicleFeeSeparate
        if (this.$store.state && this.$store.state.VEHICLE_SEPARATE !== undefined) {
          isVehicleFeeSeparate = this.$store.state.VEHICLE_SEPARATE
        }
        const vehicleSeparate = localStorage.getItem('vehicle_separate')
        if (vehicleSeparate !== null) {
          isVehicleFeeSeparate = JSON.parse(vehicleSeparate)
        }
        const shouldShow = hasGuests && (hasVehicles || isVehicleFeeSeparate)
        if (this.showCartButton !== shouldShow) {
          this.showCartButton = shouldShow
        }
        if (!shouldShow && this.sidebarOpen) {
          this.sidebarOpen = false
          document.body.style.overflow = ''
        }
      },
      updateCartCount() {
        const facilities = JSON.parse(localStorage.getItem('facility_data') || '[]')
        const vehicles = JSON.parse(localStorage.getItem('vehicle_data') || '[]')
        const conservation = JSON.parse(localStorage.getItem('conservation_data') || '[]')
        const activities = JSON.parse(localStorage.getItem('activity_data') || '[]')
        let total = 0
        if (conservation.length > 0) total += 1
        if (vehicles.length > 0) total += 1
        const uniqueFacilities = new Set()
        facilities.forEach(f => {
          uniqueFacilities.add(f.id || f.public_id || f.facility_id || f.name)
        })
        total += uniqueFacilities.size
        const uniqueActivities = new Set()
        activities.forEach(a => {
          const key = `${a.name || a.activity_name}_${a.date}_${a.time || a.start_time}`
          uniqueActivities.add(key)
        })
        total += uniqueActivities.size
        this.cartItemCount = total
      },
      handleCartUpdated() {
        this.updateCartCount()
        this.checkCartButtonVisibility()
        this.refreshSidebarContent()
      },
      refreshSidebarContent() {
        this.$nextTick(() => {
          if (this.$refs.sidebar) {
            this.$refs.sidebar.$forceUpdate()
          }
        })
      },
      initializeStorage() {
        if (!localStorage.getItem('user_data')) {
          localStorage.setItem('user_data', JSON.stringify({
            currency: 'KES',
            type: 'GB601X10',
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            destination: '',
            check_in: '',
            check_out: '',
            session_id: 'Guest'
          }))
        }
        if (!localStorage.getItem('vehicle_data')) localStorage.setItem('vehicle_data', JSON.stringify([]))
        if (!localStorage.getItem('conservation_data')) localStorage.setItem('conservation_data', JSON.stringify([]))
        if (!localStorage.getItem('facility_data')) localStorage.setItem('facility_data', JSON.stringify([]))
        if (!localStorage.getItem('activity_data')) localStorage.setItem('activity_data', JSON.stringify([]))
        if (!localStorage.getItem('vehicle_separate')) localStorage.setItem('vehicle_separate', JSON.stringify(false))
        if (!localStorage.getItem('guests')) localStorage.setItem('guests', JSON.stringify({ adults: 0, children: 0 }))
      },

      handleStorageChange(event) {
        const dataKeys = [
          'activity_data',
          'facility_data',
          'conservation_data',
          'vehicle_data',
          'vehicle_separate',
          'guests'
        ]
        if (dataKeys.includes(event.key)) {
          if (this.sidebarOpen) {
            this.enforceProtectedItems()
          }
          this.loadStateFromStorage()
          this.commitStoredDataToVuex()
          this.updateCartCount()
          this.checkCartButtonVisibility()
          this.refreshSidebarContent()
        }
      },

      showGuide() {
        if (this.$refs.userGuide) {
          this.$refs.userGuide.openGuide()
        }
      },
      onGuideFinished() { }
    }
  }
</script>

<style scoped>
  .index-container {
    padding: 0;
    background: #f5f7fa;
    min-height: 100vh;
  }

  .booking-wrapper {
    display: flex;
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
    gap: 24px;
    position: relative;
  }

  .sidebar-wrapper {
    position: fixed;
    top: 0;
    right: 0;
    width: 340px;
    height: 100vh;
    background: white;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    transition: transform 0.3s ease;
  }

  .slide-enter-active, .slide-leave-active {
    transition: transform 0.3s ease;
  }

  .slide-enter, .slide-leave-to {
    transform: translateX(100%);
  }

  .sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: #74a533;
    color: white;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .sidebar-title {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .sidebar-badge {
    background: #ff4444;
    color: white;
    border-radius: 50%;
    min-width: 22px;
    height: 22px;
    font-size: 12px;
    font-weight: bold;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    margin-left: 10px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }

  .close-sidebar-btn {
    color: white !important;
    background: rgba(255,255,255,0.2) !important;
    border-radius: 50% !important;
  }

  .sidebar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.25s ease;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  .main-content {
    flex: 1;
    min-width: 0;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    padding: 24px;
    margin-top: 0;
  }

  .help-btn-wrapper {
    position: fixed;
    bottom: 80px;
    right: 24px;
    z-index: 997;
  }

  .help-btn {
    background: linear-gradient(135deg, #74a533, #5a8a2a) !important;
    color: white !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
    transition: none !important;
    width: 40px !important;
    height: 40px !important;
  }

    .help-btn:hover {
      background: linear-gradient(135deg, #5a8a2a, #4a7a22) !important;
      box-shadow: 0 6px 20px rgba(116, 165, 51, 0.4) !important;
    }

    .help-btn .v-icon {
      font-size: 22px !important;
    }

  .protected-snackbar > > > .v-snackbar__content {
    font-size: 0.85rem;
    line-height: 1.35;
  }

  /* WILDLIFE CAROUSEL */
  .wildlife-carousel-wrapper {
    margin-bottom: 20px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  .wildlife-carousel {
    border-radius: 12px;
  }

    .wildlife-carousel > > > .v-carousel__controls {
      background: rgba(0, 0, 0, 0.25);
      padding: 8px 0;
    }

      .wildlife-carousel > > > .v-carousel__controls .v-btn {
        color: rgba(255, 255, 255, 0.7);
      }

      .wildlife-carousel > > > .v-carousel__controls .v-btn--active {
        color: #74a533;
      }

  .wildlife-carousel-item {
    position: relative;
  }

  .wildlife-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 40px 30px 30px 30px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
    color: white;
    text-align: left;
  }

  .wildlife-caption h2 {
    font-size: 2rem;
    font-weight: 700;
    margin: 0 0 4px 0;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    letter-spacing: 0.5px;
  }

  .wildlife-caption p {
    font-size: 1rem;
    margin: 0;
    opacity: 0.9;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    font-weight: 400;
  }

  .booking-stepper {
    width: 100%;
  }

  .stepper-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 0;
    border-bottom: 2px solid #e8e8e8;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .step-item {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 80px;
    position: relative;
    cursor: default;
  }

    .step-item.clickable {
      cursor: pointer;
    }

  .step-circle {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #e8e8e8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: #666;
    font-size: 14px;
    flex-shrink: 0;
    transition: all 0.3s ease;
  }

  .step-item.active .step-circle {
    background: #74a533;
    color: white;
    box-shadow: 0 4px 12px rgba(116, 165, 51, 0.3);
  }

  .step-item.completed .step-circle {
    background: #74a533;
    color: white;
  }

  .step-check {
    font-size: 16px;
  }

  .step-label {
    font-size: 12px;
    font-weight: 500;
    color: #666;
    margin-left: 8px;
    white-space: nowrap;
    transition: color 0.3s ease;
  }

  .step-item.active .step-label {
    color: #74a533;
    font-weight: 600;
  }

  .step-item.completed .step-label {
    color: #74a533;
  }

  .step-line {
    flex: 1;
    height: 2px;
    background: #e8e8e8;
    margin: 0 10px;
    min-width: 20px;
  }

  .step-item.completed .step-line {
    background: #74a533;
  }

  .step-panel {
    animation: fadeIn 0.3s ease;
    display: flex;
    flex-direction: column;
    min-height: 300px;
    position: relative;
    background: white;
    border-radius: 8px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .step-actions-top {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 2px solid #f0f0f0;
    background: white;
    flex-shrink: 0;
    flex-wrap: wrap;
    min-height: 60px;
    border-radius: 8px 8px 0 0;
  }

  .step-body {
    flex: 1;
    padding: 16px;
  }

  .step-card {
    padding: 16px;
    background: #f9fafb;
    border-radius: 12px;
  }

  .step-header-section {
    margin-bottom: 20px;
    text-align: center;
    padding-top: 8px;
  }

  .step-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #333;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 8px;
  }

  .step-icon {
    font-size: 32px !important;
  }

  .step-subtitle {
    font-size: 1rem;
    color: #666;
    margin: 0;
  }

  .step-btn {
    min-width: 180px;
    text-transform: none;
    font-weight: 600;
    letter-spacing: 0.5px;
    border-radius: 8px;
    padding: 8px 24px;
  }

  .step-btn-back {
    color: #666;
    text-transform: none;
    font-weight: 500;
  }

    .step-btn-back:hover {
      color: #74a533;
    }

  .step-btn-skip {
    color: #999;
    text-transform: none;
    font-weight: 500;
  }

    .step-btn-skip:hover {
      color: #74a533;
    }

  .step-btn-reset {
    color: #c62828;
    text-transform: none;
    font-weight: 500;
  }

    .step-btn-reset:hover {
      color: #b71c1c;
    }

  .date-picker-field > > > .v-input__prepend-outer {
    color: #74a533;
  }

  .guest-summary, .vehicle-summary {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 0;
    border-top: 1px solid #e8e8e8;
    margin-top: 8px;
  }

  .guest-total, .vehicle-total {
    font-weight: 600;
    font-size: 1.1rem;
    color: #333;
  }

  .guest-badge, .vehicle-badge {
    background: #74a533;
    color: white;
    padding: 4px 16px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .terms-body .step-body {
    padding-bottom: 16px;
  }

  .terms-card {
    background: #f9fafb;
  }

  .section-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1b5e20;
    margin: 24px 0 16px 0;
  }

  .terms-text {
    font-size: 1rem;
    line-height: 1.6;
    color: #424242;
    margin-bottom: 16px;
  }

  .terms-list {
    padding: 0;
    background: transparent;
  }

  .terms-list-item {
    font-size: 1rem;
    line-height: 1.6;
    color: #424242;
    margin-bottom: 8px;
  }

  .checkout-card {
    background: #f8faf5;
  }

  .checkout-section-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #74a533;
  }

  .checkout-summary-list {
    background: white;
    border-radius: 8px;
    padding: 8px 0;
  }

    .checkout-summary-list .v-list-item {
      border-bottom: 1px solid #f0f0f0;
    }

      .checkout-summary-list .v-list-item:last-child {
        border-bottom: none;
      }

  .checkout-total {
    background: white;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
  }

  .total-amount {
    font-size: 2.2rem;
    font-weight: 700;
    color: #74a533;
    margin-bottom: 20px;
  }

  .checkout-btn {
    text-transform: none;
    font-weight: 600;
    letter-spacing: 0.5px;
    border-radius: 8px;
    padding: 12px;
  }

  @media (max-width: 960px) {
    .booking-wrapper {
      flex-direction: column;
      padding: 12px;
      gap: 12px;
    }

    .main-content {
      padding: 16px;
      border-radius: 8px;
    }

    .step-title {
      font-size: 1.4rem;
    }

    .step-label {
      font-size: 10px;
    }

    .step-circle {
      width: 30px;
      height: 30px;
      font-size: 12px;
    }

    .stepper-header {
      padding: 12px 0;
      gap: 6px;
    }

    .step-item {
      min-width: 50px;
    }

    .step-btn {
      min-width: 120px;
      font-size: 0.85rem;
      padding: 6px 16px;
    }

    .help-btn-wrapper {
      bottom: 70px;
      right: 16px;
    }

    .step-actions-top {
      padding: 10px 12px;
      gap: 8px;
    }

    .wildlife-carousel-wrapper {
      margin-bottom: 16px;
    }

    .wildlife-caption h2 {
      font-size: 1.5rem;
    }

    .wildlife-caption p {
      font-size: 0.9rem;
    }

    .wildlife-overlay {
      padding: 28px 20px 20px 20px;
    }
  }

  @media (max-width: 600px) {
    .booking-wrapper {
      padding: 8px;
    }

    .main-content {
      padding: 12px;
    }

    .step-title {
      font-size: 1.1rem;
    }

    .step-subtitle {
      font-size: 0.85rem;
    }

    .step-label {
      font-size: 8px;
    }

    .step-circle {
      width: 26px;
      height: 26px;
      font-size: 10px;
    }

    .step-item {
      min-width: 40px;
    }

    .step-line {
      margin: 0 4px;
      min-width: 8px;
    }

    .step-actions-top {
      flex-direction: column;
      align-items: stretch;
      gap: 6px;
      padding: 8px 10px;
    }

    .step-btn {
      min-width: 100%;
    }

    .step-btn-back {
      order: 10;
    }

    .step-btn-skip {
      order: 11;
    }

    .step-btn-reset {
      order: 12;
    }

    .help-btn-wrapper {
      bottom: 65px;
      right: 12px;
    }

    .help-btn {
      width: 36px !important;
      height: 36px !important;
    }

    .total-amount {
      font-size: 1.6rem;
    }

    .sidebar-wrapper {
      width: 85%;
      max-width: 320px;
    }

    .checkout-total {
      padding: 12px;
    }

    .section-title {
      font-size: 1.2rem;
    }

    .terms-text, .terms-list-item {
      font-size: 1rem;
    }

    .wildlife-carousel-wrapper {
      display: none !important;
    }
  }
</style>
